import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Home, CheckCircle2, Circle, Trophy, Lightbulb, Award, ArrowLeft } from "lucide-react";
import { microLessonCategories } from "@/data/microLessons";
import { MicroLesson } from "@/types/microLearning";
import { useGamificationDB } from "@/hooks/useGamificationDB";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import womanReading from "@/assets/woman-reading-book.jpg";

const MicroLearning = () => {
  const navigate = useNavigate();
  const gamification = useGamificationDB();
  const [selectedLesson, setSelectedLesson] = useState<MicroLesson | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>("");
  const [quizAnswer, setQuizAnswer] = useState<number | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [showQuizResult, setShowQuizResult] = useState(false);

  const completedMicroLessons = (gamification as any).completedMicroLessons || [];

  const handleCompleteLesson = (lesson: MicroLesson, categoryId: string) => {
    if (!completedMicroLessons.includes(lesson.id)) {
      gamification.completeMicroLesson(lesson.id, categoryId);
      toast.success("Micro-lesson completed! +10 points", {
        description: "Keep learning to unlock more badges!"
      });

      // Check if category is complete and unlock badge
      const category = microLessonCategories.find(c => c.id === categoryId);
      if (category) {
        const categoryCompletedLessons = category.lessons.filter(l => 
          completedMicroLessons.includes(l.id) || l.id === lesson.id
        );
        if (categoryCompletedLessons.length === category.lessons.length) {
          // Find the badge ID from any lesson in this category
          const badgeId = category.lessons.find(l => l.relatedBadgeId)?.relatedBadgeId;
          if (badgeId) {
            gamification.unlockCategoryBadge(badgeId);
            const badge = gamification.badges.find(b => b.id === badgeId);
            if (badge && !badge.unlocked) {
              toast.success(`Badge Unlocked: ${badge.name}!`, {
                description: badge.description,
                icon: badge.icon
              });
            }
          }
        }
      }
    }
  };

  const handleQuizSubmit = (lesson: MicroLesson, categoryId: string) => {
    if (quizAnswer === null || !lesson.quiz || !lesson.quiz[currentQuestionIndex]) return;
    
    setShowQuizResult(true);
    
    const currentQuestion = lesson.quiz[currentQuestionIndex];
    if (quizAnswer === currentQuestion.correctIndex) {
      toast.success("Correct answer! +5 bonus points");
    }
  };

  const handleNextQuestion = () => {
    if (selectedLesson?.quiz && currentQuestionIndex < selectedLesson.quiz.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setQuizAnswer(null);
      setShowQuizResult(false);
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      setQuizAnswer(null);
      setShowQuizResult(false);
    }
  };

  if (selectedLesson) {
    const category = microLessonCategories.find(c => c.id === selectedCategoryId);
    const isCompleted = completedMicroLessons.includes(selectedLesson.id);

    return (
      <div className="min-h-screen bg-background">
        <div className="bg-gradient-hero text-white">
          <div className="container mx-auto px-4 py-8">
            <div className="flex gap-2 mb-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  setSelectedLesson(null);
                  setQuizAnswer(null);
                  setCurrentQuestionIndex(0);
                  setShowQuizResult(false);
                }}
                className="text-white hover:bg-white/10"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <Link to="/">
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-white hover:bg-white/10"
                >
                  <Home className="h-5 w-5" />
                </Button>
              </Link>
            </div>
            <div className="flex items-center gap-3 mb-2">
              <span className="text-4xl">{category?.icon}</span>
              <div>
                <h1 className="text-3xl font-bold">{selectedLesson.title}</h1>
                {isCompleted && (
                  <div className="mt-2">
                    <Badge variant="secondary" className="bg-green-500/20 text-white border-0">
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      Completed
                    </Badge>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 py-8 max-w-3xl">
          <Tabs defaultValue="lesson" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="lesson">Lesson</TabsTrigger>
              <TabsTrigger value="quiz" disabled={!selectedLesson.quiz}>
                Quick Quiz {!selectedLesson.quiz && "(Coming Soon)"}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="lesson">
              <Card>
                <CardContent className="pt-6">
                  <div className="prose prose-sm max-w-none">
                    {selectedLesson.content.split('\n').map((paragraph, idx) => {
                      if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                        const text = paragraph.slice(2, -2);
                        if (text.includes(':')) {
                          return <h3 key={idx} className="text-lg font-semibold mt-6 mb-3 text-foreground">{text}</h3>;
                        }
                        return <p key={idx} className="font-semibold text-foreground my-3">{text}</p>;
                      }
                      if (paragraph.startsWith('-')) {
                        return (
                          <li key={idx} className="ml-6 my-2 text-muted-foreground">
                            {paragraph.slice(2)}
                          </li>
                        );
                      }
                      if (paragraph.trim() === '') return null;
                      return <p key={idx} className="my-4 text-muted-foreground leading-relaxed">{paragraph}</p>;
                    })}
                  </div>

                  <div className="mt-8 p-4 bg-primary/5 rounded-lg border-l-4 border-primary">
                    <div className="flex items-start gap-3">
                      <Lightbulb className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-foreground mb-1">Key Takeaway</p>
                        <p className="text-sm text-muted-foreground">{selectedLesson.keyTakeaway}</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 flex justify-center">
                    <Button
                      size="lg"
                      onClick={() => handleCompleteLesson(selectedLesson, selectedCategoryId)}
                      disabled={isCompleted}
                      className="min-w-[200px]"
                    >
                      {isCompleted ? (
                        <>
                          <CheckCircle2 className="h-4 w-4 mr-2" />
                          Completed
                        </>
                      ) : (
                        <>
                          <Award className="h-4 w-4 mr-2" />
                          Mark Complete
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {selectedLesson.quiz && selectedLesson.quiz.length > 0 && (
              <TabsContent value="quiz">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg">Test Your Knowledge</CardTitle>
                        <CardDescription>
                          Question {currentQuestionIndex + 1} of {selectedLesson.quiz.length}
                        </CardDescription>
                      </div>
                      {selectedLesson.quiz.length > 1 && (
                        <div className="flex gap-1">
                          {selectedLesson.quiz.map((_, idx) => (
                            <div
                              key={idx}
                              className={`h-2 w-8 rounded-full transition-colors ${
                                idx === currentQuestionIndex
                                  ? 'bg-primary'
                                  : idx < currentQuestionIndex
                                  ? 'bg-primary/50'
                                  : 'bg-muted'
                              }`}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="font-medium mb-4">
                      {selectedLesson.quiz[currentQuestionIndex].question}
                    </p>
                    
                    <div className="space-y-3">
                      {selectedLesson.quiz[currentQuestionIndex].options.map((option, idx) => (
                        <button
                          key={idx}
                          onClick={() => !showQuizResult && setQuizAnswer(idx)}
                          disabled={showQuizResult}
                          className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                            !showQuizResult && quizAnswer === idx
                              ? 'border-primary bg-primary/5'
                              : showQuizResult && quizAnswer === idx && idx === selectedLesson.quiz![currentQuestionIndex].correctIndex
                              ? 'border-green-500 bg-green-50 dark:bg-green-950'
                              : showQuizResult && quizAnswer === idx && idx !== selectedLesson.quiz![currentQuestionIndex].correctIndex
                              ? 'border-red-500 bg-red-50 dark:bg-red-950'
                              : 'border-border hover:border-primary/50'
                          } ${showQuizResult ? 'cursor-default' : 'cursor-pointer'}`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                              quizAnswer === idx && !showQuizResult ? 'border-primary bg-primary' : 'border-border'
                            }`}>
                              {quizAnswer === idx && !showQuizResult && <div className="w-2 h-2 bg-white rounded-full" />}
                            </div>
                            <span className="flex-1">{option}</span>
                          </div>
                        </button>
                      ))}
                    </div>

                    {showQuizResult && selectedLesson.quiz[currentQuestionIndex] && (
                      <div className={`mt-4 p-4 rounded-lg ${
                        quizAnswer === selectedLesson.quiz[currentQuestionIndex].correctIndex
                          ? 'bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800'
                          : 'bg-orange-50 dark:bg-orange-950 border border-orange-200 dark:border-orange-800'
                      }`}>
                        <p className="font-semibold mb-2">
                          {quizAnswer === selectedLesson.quiz[currentQuestionIndex].correctIndex ? 'Correct!' : 'Learning Moment'}
                        </p>
                        <p className="text-sm">{selectedLesson.quiz[currentQuestionIndex].explanation}</p>
                      </div>
                    )}

                    <div className="mt-6 flex justify-between items-center gap-3">
                      <Button
                        variant="outline"
                        onClick={handlePreviousQuestion}
                        disabled={currentQuestionIndex === 0}
                      >
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Previous
                      </Button>
                      
                      <div className="flex-1 flex justify-center gap-3">
                        {!showQuizResult ? (
                          <Button
                            size="lg"
                            onClick={() => handleQuizSubmit(selectedLesson, selectedCategoryId)}
                            disabled={quizAnswer === null}
                            className="min-w-[200px]"
                          >
                            Submit Answer
                          </Button>
                        ) : quizAnswer !== selectedLesson.quiz![currentQuestionIndex].correctIndex ? (
                          <Button
                            size="lg"
                            variant="outline"
                            onClick={() => {
                              setQuizAnswer(null);
                              setShowQuizResult(false);
                            }}
                            className="min-w-[200px]"
                          >
                            Try Again
                          </Button>
                        ) : null}
                      </div>

                      <Button
                        variant="outline"
                        onClick={handleNextQuestion}
                        disabled={!selectedLesson.quiz || currentQuestionIndex >= selectedLesson.quiz.length - 1}
                      >
                        Next
                        <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            )}
          </Tabs>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background relative">
      {/* Background Image with Fade */}
      <div 
        className="fixed inset-0 z-0"
        style={{
          backgroundImage: `url(${womanReading})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-background/95 via-background/90 to-background/95" />
      </div>

      {/* Content */}
      <div className="relative z-10">
        <div className="bg-gradient-hero text-white">
          <div className="container mx-auto px-4 py-12">
            <Link to="/">
              <Button
                variant="ghost"
                size="icon"
                className="text-white hover:bg-white/10 mb-6"
              >
                <Home className="h-5 w-5" />
              </Button>
            </Link>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-5xl font-bold mb-4">Micro-Learning</h1>
              <p className="text-xl text-white/90 mb-4">
                Quick, powerful lessons in 2-3 minutes. Perfect for busy days.
              </p>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Trophy className="h-5 w-5" />
                  <span className="text-sm">
                    {completedMicroLessons.length} lessons completed
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Award className="h-5 w-5" />
                  <span className="text-sm">
                    {gamification.badges.filter(b => b.unlocked).length} badges earned
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="space-y-8 max-w-5xl mx-auto">
          {microLessonCategories.map((category) => {
            const completedInCategory = category.lessons.filter(l => 
              completedMicroLessons.includes(l.id)
            ).length;
            const progressPercent = (completedInCategory / category.lessons.length) * 100;

            return (
              <Card key={category.id} className="overflow-hidden border-2">
                <CardHeader className="bg-gradient-to-r from-primary/5 to-transparent">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-4xl">{category.icon}</span>
                      <div>
                        <CardTitle className="text-2xl">{category.name}</CardTitle>
                        <CardDescription className="text-base mt-1">
                          {category.description}
                        </CardDescription>
                      </div>
                    </div>
                    <Badge variant="secondary" className="text-sm">
                      {completedInCategory}/{category.lessons.length}
                    </Badge>
                  </div>
                  <div className="mt-4">
                    <Progress value={progressPercent} className="h-2" />
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="grid gap-3 md:grid-cols-2">
                    {category.lessons.map((lesson) => {
                      const isCompleted = completedMicroLessons.includes(lesson.id);
                      
                      return (
                        <button
                          key={lesson.id}
                          onClick={() => {
                            setSelectedLesson(lesson);
                            setSelectedCategoryId(category.id);
                          }}
                          className="text-left p-4 rounded-lg border-2 hover:border-primary transition-all hover:shadow-md group"
                        >
                          <div className="flex items-start gap-3">
                            <div className="mt-1">
                              {isCompleted ? (
                                <CheckCircle2 className="h-5 w-5 text-green-500" />
                              ) : (
                                <Circle className="h-5 w-5 text-muted-foreground group-hover:text-primary" />
                              )}
                            </div>
                            <div className="flex-1">
                              <h3 className="font-semibold mb-1 group-hover:text-primary transition-colors">
                                {lesson.title}
                              </h3>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                   </div>
                </CardContent>
              </Card>
            );
          })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MicroLearning;
